import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    Switch,
    message,
    Tooltip,
    Card,
    Row,
    Col,
    Statistic,
    Tabs,
    Descriptions,
    List,
    Divider,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    TeamOutlined,
    DollarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    UserAddOutlined,
    UserDeleteOutlined,
} from '@ant-design/icons';
import { departmentAPI, userAPI } from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;

const DepartmentManagementPage = () => {
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);  // ⭐ 추가
    const [editingDepartment, setEditingDepartment] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);    // ⭐ 추가
    const [budgetModalVisible, setBudgetModalVisible] = useState(false);
    const [budgets, setBudgets] = useState([]);
    const [budgetLoading, setBudgetLoading] = useState(false);
    const [currentYear] = useState(new Date().getFullYear());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [filters, setFilters] = useState({});
    const [form] = Form.useForm();
    const [budgetForm] = Form.useForm();
    
    // 다중 선택 관련 state
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false);

    useEffect(() => {
        fetchDepartments();
        fetchUsers();
    }, []);

    const fetchDepartments = async (filterParams = {}) => {
        setLoading(true);
        try {
            const response = await departmentAPI.getAll({ ...filters, ...filterParams });
            setDepartments(response.data.departments || []);
        } catch (error) {
            message.error('부서 목록 조회 실패');
            console.error('Fetch departments error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await userAPI.getAll({ is_active: 'true' });
            setUsers(response.data.users || []);
        } catch (error) {
            console.error('Fetch users error:', error);
        }
    };

    // ⭐ 부서 상세보기
    const handleViewDetail = async (record) => {
        console.log('상세보기 클릭:', record);  // ⭐ 디버깅
        try {
            setLoading(true);
            const [deptResponse, budgetResponse] = await Promise.all([
                departmentAPI.getById(record.id),
                departmentAPI.getBudgets(record.id)
            ]);
            
            const departmentWithBudgets = {
                ...deptResponse.data.department,
                budgets: budgetResponse.data.budgets
            };
            
            console.log('부서 상세 데이터:', departmentWithBudgets);  // ⭐ 디버깅
            setSelectedDepartment(departmentWithBudgets);
            setDetailModalVisible(true);
        } catch (error) {
            console.error('부서 상세 조회 에러:', error);  // ⭐ 디버깅
            message.error(error.response?.data?.message || '부서 상세 조회 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingDepartment(null);
        form.resetFields();
        form.setFieldsValue({
            is_active: true
        });
        setModalVisible(true);
    };

    const handleEdit = async (record) => {
        setEditingDepartment(record);

        // ⭐ 부서 상세 정보 및 예산 조회
        try {
            const [deptResponse, budgetResponse] = await Promise.all([
                departmentAPI.getById(record.id),
                departmentAPI.getBudgets(record.id)
            ]);
            
            const deptDetail = deptResponse.data.department;
            const budgets = budgetResponse.data.budgets || [];
            
            // 최신 연도 예산의 인건비 예산 계산
            const currentYear = new Date().getFullYear();
            const currentBudget = budgets.find(b => b.year === currentYear) || budgets[0];
            const totalLaborBudget = currentBudget ? 
                parseFloat(currentBudget.labor_budget || currentBudget.annual_budget || 0) : 0;

            form.setFieldsValue({
                name: deptDetail.name,
                code: deptDetail.code,
                description: deptDetail.description,
                manager_id: deptDetail.manager_id,
                is_active: deptDetail.is_active,
            });

            // 부서원 정보와 예산 정보를 editingDepartment에 포함
            setEditingDepartment({
                ...record,
                employees: deptDetail.employees,
                member_count: deptDetail.member_count,
                budget_analysis: deptDetail.budget_analysis,
                current_labor_budget: totalLaborBudget
            });

        } catch (error) {
            console.error('부서 상세 조회 실패:', error);
            form.setFieldsValue({
                name: record.name,
                code: record.code,
                description: record.description,
                manager_id: record.manager_id,
                is_active: record.is_active,
            });
        }

        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (editingDepartment) {
                await departmentAPI.update(editingDepartment.id, values);
                message.success('부서 정보가 수정되었습니다');
            } else {
                await departmentAPI.create(values);
                message.success('부서가 생성되었습니다');
            }

            setModalVisible(false);
            form.resetFields();
            fetchDepartments();
        } catch (error) {
            if (error.errorFields) {
                message.error('필수 항목을 입력해주세요');
            } else {
                message.error(error.response?.data?.message || '처리 실패');
            }
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '부서 삭제',
            content: `${record.name} 부서를 삭제하시겠습니까? 소속된 사용자가 있으면 삭제할 수 없습니다.`,
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await departmentAPI.delete(record.id);
                    message.success('부서가 삭제되었습니다');
                    fetchDepartments();
                } catch (error) {
                    message.error(error.response?.data?.message || '삭제 실패');
                }
            },
        });
    };

    // ⭐ 부서원 추가
    const handleAddMember = async (userId) => {
        try {
            await departmentAPI.addMember(selectedDepartment.id, { user_id: userId });
            message.success('부서원이 추가되었습니다');

            // 부서 상세 다시 조회
            const response = await departmentAPI.getById(selectedDepartment.id);
            const updatedDepartment = response.data.department;
            setSelectedDepartment(updatedDepartment);
            fetchDepartments();
            
            // 기존 예산들의 employee_count 업데이트
            await updateExistingBudgetEmployeeCount(updatedDepartment.id, updatedDepartment.member_count);
            await fetchBudgets(updatedDepartment.id);
        } catch (error) {
            message.error(error.response?.data?.message || '부서원 추가 실패');
        }
    };

    // ⭐ 부서원 제거
    const handleRemoveMember = (user) => {
        console.log('=== 부서원 제거 클릭 ===');
        console.log('selectedDepartment.id:', selectedDepartment.id);
        console.log('user.id:', user.id);

        Modal.confirm({
            title: '부서원 제거',
            content: `${user.name}님을 부서에서 제거하시겠습니까?`,
            okText: '제거',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    console.log('API 호출:', selectedDepartment.id, user.id);
                    await departmentAPI.removeMember(selectedDepartment.id, user.id);
                    message.success('부서원이 제거되었습니다');

                    // 부서 상세 다시 조회
                    const response = await departmentAPI.getById(selectedDepartment.id);
                    const updatedDepartment = response.data.department;
                    setSelectedDepartment(updatedDepartment);
                    fetchDepartments();
                    
                    // 기존 예산들의 employee_count 업데이트
                    await updateExistingBudgetEmployeeCount(updatedDepartment.id, updatedDepartment.member_count);
                    await fetchBudgets(updatedDepartment.id);
                } catch (error) {
                    console.error('부서원 제거 에러:', error);
                    console.error('에러 응답:', error.response);
                    message.error(error.response?.data?.message || '부서원 제거 실패');
                }
            },
        });
    };

    // 예산 관리 모달 열기
    const handleBudgetManagement = async (department) => {
        setSelectedDepartment(department);
        setBudgetModalVisible(true);
        await fetchBudgets(department.id);
    };

    // 부서 예산 조회
    const fetchBudgets = async (departmentId, year = null) => {
        setBudgetLoading(true);
        try {
            const params = year ? { year } : {};
            const response = await departmentAPI.getBudgets(departmentId, params);
            setBudgets(response.data.budgets);
        } catch (error) {
            message.error('예산 정보 조회 실패');
        } finally {
            setBudgetLoading(false);
        }
    };

    // 예산 수정을 위한 폼 데이터 설정
    // 기존 예산들의 employee_count 업데이트 함수
    const updateExistingBudgetEmployeeCount = async (departmentId, newEmployeeCount) => {
        try {
            // 현재 부서의 모든 예산 가져오기
            const budgetResponse = await departmentAPI.getBudgets(departmentId);
            const existingBudgets = budgetResponse.data.budgets || [];
            
            // 각 예산의 employee_count 업데이트
            for (const budget of existingBudgets) {
                const updatedBudgetData = {
                    year: budget.year,
                    labor_budget: budget.labor_budget || budget.annual_budget,
                    order_target: budget.order_target,
                    notes: budget.notes,
                    employee_count: newEmployeeCount
                };
                
                await departmentAPI.upsertBudget(departmentId, updatedBudgetData);
            }
        } catch (error) {
            console.error('예산 employee_count 업데이트 실패:', error);
        }
    };

    const handleBudgetEdit = (record) => {
        budgetForm.setFieldsValue({
            year: record.year,
            labor_budget: parseFloat(record.labor_budget || record.annual_budget || 0),
            order_target: parseFloat(record.order_target || 0),
            notes: record.notes
        });
    };

    // 예산 생성/수정
    const handleBudgetSubmit = async (values) => {
        try {
            // 현재 부서원 수를 자동으로 포함
            const budgetData = {
                ...values,
                annual_budget: values.labor_budget, // 호환성을 위해 annual_budget도 설정
                employee_count: selectedDepartment.member_count || 0
            };
            
            await departmentAPI.upsertBudget(selectedDepartment.id, budgetData);
            message.success('예산 정보가 저장되었습니다');
            budgetForm.resetFields();
            await fetchBudgets(selectedDepartment.id);
        } catch (error) {
            message.error(error.response?.data?.message || '예산 저장 실패');
        }
    };

    // 예산 삭제
    const handleBudgetDelete = async (budgetId) => {
        try {
            await departmentAPI.deleteBudget(selectedDepartment.id, budgetId);
            message.success('예산 정보가 삭제되었습니다');
            await fetchBudgets(selectedDepartment.id);
        } catch (error) {
            message.error(error.response?.data?.message || '예산 삭제 실패');
        }
    };

    // 연도 변경 핸들러
    const handleYearChange = (year) => {
        setSelectedYear(year);
        if (selectedDepartment) {
            fetchBudgets(selectedDepartment.id, year);
        }
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        if (!value) delete newFilters[key];
        setFilters(newFilters);
        fetchDepartments(newFilters);
        // 필터 변경 시 선택 초기화
        setSelectedRowKeys([]);
        setSelectedDepartments([]);
    };

    // 다중 선택 관련 함수들
    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
        const newSelectedDepartments = departments.filter(dept => newSelectedRowKeys.includes(dept.id));
        setSelectedDepartments(newSelectedDepartments);
    };

    const handleBulkDelete = () => {
        if (selectedDepartments.length === 0) {
            message.warning('삭제할 부서를 선택하세요');
            return;
        }
        
        // 부서원이 있는 부서가 있는지 확인
        const departmentsWithMembers = selectedDepartments.filter(dept => dept.member_count > 0);
        if (departmentsWithMembers.length > 0) {
            message.error(`부서원이 있는 부서는 삭제할 수 없습니다. (${departmentsWithMembers.map(d => d.name).join(', ')})`);
            return;
        }
        
        setBulkDeleteModalVisible(true);
    };

    const confirmBulkDelete = async () => {
        try {
            const deptIds = selectedRowKeys;
            
            // 백엔드 API 호출 (각 부서별로 개별 삭제 호출)
            const deletePromises = deptIds.map(id => departmentAPI.delete(id));
            await Promise.all(deletePromises);

            message.success(`${selectedDepartments.length}개의 부서가 삭제되었습니다`);
            
            // 상태 초기화 및 새로고침
            setSelectedRowKeys([]);
            setSelectedDepartments([]);
            setBulkDeleteModalVisible(false);
            fetchDepartments();
        } catch (error) {
            message.error(error.response?.data?.message || '일괄 삭제 실패');
        }
    };

    const clearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedDepartments([]);
    };

    const columns = [
        {
            title: '부서 코드',
            dataIndex: 'code',
            key: 'code',
            width: 120,
        },
        {
            title: '부서명',
            dataIndex: 'name',
            key: 'name',
            width: 200,
        },
        {
            title: '부서장',
            dataIndex: ['manager', 'name'],
            key: 'manager',
            width: 150,
            render: (name, record) => (
                record.manager ? (
                    <Space direction="vertical" size={0}>
                        <span>{name}</span>
                        <span style={{ fontSize: 12, color: '#888' }}>
                            {record.manager.employee_id}
                        </span>
                    </Space>
                ) : (
                    <span style={{ color: '#ccc' }}>미지정</span>
                )
            ),
        },
        {
            title: '인원',
            dataIndex: 'member_count',
            key: 'member_count',
            width: 80,
            align: 'center',
            render: (count) => (
                <Tag color="blue">{count || 0}명</Tag>
            ),
        },
        {
            title: '예산',
            dataIndex: 'budget',
            key: 'budget',
            width: 150,
            align: 'right',
            render: (budget) => (
                <span>{parseInt(budget || 0).toLocaleString()}원</span>
            ),
        },
        {
            title: '설명',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: '상태',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 80,
            align: 'center',
            render: (is_active) =>
                is_active ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        활성
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="default">
                        비활성
                    </Tag>
                ),
        },
        {
            title: '작업',
            key: 'action',
            width: 240,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="상세보기">
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetail(record)}
                        />
                    </Tooltip>
                    <Tooltip title="예산 관리">
                        <Button
                            type="link"
                            size="small"
                            icon={<DollarOutlined />}
                            onClick={() => handleBudgetManagement(record)}
                        />
                    </Tooltip>
                    <Tooltip title="수정">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="삭제">
                        <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const statistics = {
        total: departments.length,
        active: departments.filter((d) => d.is_active).length,
        inactive: departments.filter((d) => !d.is_active).length,
        total_budget: departments.reduce((sum, d) => sum + parseFloat(d.budget || 0), 0),
    };

    // ⭐ 부서에 소속되지 않은 사용자 목록
    const availableUsers = users.filter(
        user => !selectedDepartment?.employees?.find(emp => emp.id === user.id)
    );

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2>부서 관리</h2>
                <Space>
                    {selectedDepartments.length > 0 && (
                        <>
                            <Button 
                                danger 
                                icon={<DeleteOutlined />} 
                                onClick={handleBulkDelete}
                            >
                                선택 삭제 ({selectedDepartments.length}개)
                            </Button>
                            <Button onClick={clearSelection}>
                                선택 취소
                            </Button>
                        </>
                    )}
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        부서 추가
                    </Button>
                </Space>
            </div>

            {/* 통계 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="전체 부서"
                            value={statistics.total}
                            prefix={<TeamOutlined />}
                            suffix="개"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="활성 부서"
                            value={statistics.active}
                            prefix={<CheckCircleOutlined />}
                            suffix="개"
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="비활성 부서"
                            value={statistics.inactive}
                            prefix={<CloseCircleOutlined />}
                            suffix="개"
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="전체 인건비"  // ⭐ "전체 예산" → "전체 인건비"
                            value={statistics.total_budget}
                            prefix={<DollarOutlined />}
                            suffix="원"
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 필터 */}
            <Card style={{ marginBottom: 16 }}>
                <Space size="middle" wrap>
                    <Select
                        placeholder="상태"
                        style={{ width: 120 }}
                        allowClear
                        onChange={(value) => handleFilterChange('is_active', value)}
                    >
                        <Option value="true">활성</Option>
                        <Option value="false">비활성</Option>
                    </Select>
                    <Input.Search
                        placeholder="부서명, 코드 검색"
                        style={{ width: 250 }}
                        allowClear
                        onSearch={(value) => handleFilterChange('search', value)}
                    />
                </Space>
            </Card>

            {/* 부서 테이블 */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={departments}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: onSelectChange,
                        getCheckboxProps: (record) => ({
                            disabled: record.member_count > 0, // 부서원이 있는 부서는 선택 불가
                        }),
                    }}
                />
            </Card>

            {/* 부서 생성/수정 모달 */}
            <Modal
                title={editingDepartment ? '부서 수정' : '부서 생성'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setEditingDepartment(null);  // ⭐ 초기화
                }}
                width={600}
                okText={editingDepartment ? '수정' : '생성'}
                cancelText="취소"
            >
                <Form form={form} layout="vertical">
                    {/* ⭐ 수정 시 부서원 정보 표시 */}
                    {editingDepartment && editingDepartment.budget_analysis && (
                        <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff' }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title="부서원 수"
                                        value={editingDepartment.member_count || 0}
                                        suffix="명"
                                        valueStyle={{ fontSize: 18 }}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="1인당 평균 연봉"
                                        value={(() => {
                                            const totalBudget = parseFloat(editingDepartment.current_labor_budget || 0);
                                            const memberCount = parseInt(editingDepartment.member_count || 0);
                                            
                                            if (memberCount === 0 || totalBudget === 0) return 0;
                                            return Math.round(totalBudget / memberCount);
                                        })()}
                                        suffix="원"
                                        valueStyle={{ fontSize: 16 }}
                                        formatter={(value) => `${parseInt(value).toLocaleString()}`}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="1일 평균 인건비"
                                        value={(() => {
                                            const totalBudget = parseFloat(editingDepartment.current_labor_budget || 0);
                                            const memberCount = parseInt(editingDepartment.member_count || 0);
                                            
                                            if (memberCount === 0 || totalBudget === 0) return 0;
                                            return Math.round(totalBudget / memberCount / 365);
                                        })()}
                                        suffix="원"
                                        valueStyle={{ fontSize: 16, color: '#52c41a' }}
                                        formatter={(value) => `${parseInt(value).toLocaleString()}`}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    )}

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="부서명"
                                rules={[{ required: true, message: '부서명을 입력하세요' }]}
                            >
                                <Input placeholder="예: 개발팀" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="code"
                                label="부서 코드"
                                rules={[
                                    { required: true, message: '부서 코드를 입력하세요' },
                                    {
                                        pattern: /^[A-Z0-9_]+$/,
                                        message: '영문 대문자, 숫자, 언더스코어만 사용 가능합니다'
                                    }
                                ]}
                            >
                                <Input
                                    placeholder="예: DEV"
                                    disabled={!!editingDepartment}
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="설명">
                        <TextArea rows={3} placeholder="부서 설명을 입력하세요" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="manager_id" label="부서장">
                                <Select
                                    placeholder="부서장 선택"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {users.map((user) => (
                                        <Option key={user.id} value={user.id}>
                                            {user.name} ({user.employee_id})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="is_active" label="활성 상태" valuePropName="checked">
                        <Switch checkedChildren="활성" unCheckedChildren="비활성" />
                    </Form.Item>

                    {/* ⭐ 부서원 목록 표시 (수정 시만) */}
                    {editingDepartment && editingDepartment.employees && editingDepartment.employees.length > 0 && (
                        <Form.Item label="현재 부서원">
                            <List
                                size="small"
                                bordered
                                dataSource={editingDepartment.employees}
                                renderItem={(user) => (
                                    <List.Item>
                                        <Space>
                                            <Tag color="blue">{user.employee_id}</Tag>
                                            {user.name}
                                            <span style={{ color: '#888' }}>({user.position || '직급 미지정'})</span>
                                            {!user.is_active && <Tag color="default">비활성</Tag>}
                                        </Space>
                                    </List.Item>
                                )}
                                style={{ maxHeight: 200, overflow: 'auto' }}
                            />
                            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                                * 부서원 추가/제거는 상세보기 화면에서 가능합니다
                            </div>
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {/* ⭐ 부서 상세보기 모달 */}
            {selectedDepartment && (
                <Modal
                    title={`${selectedDepartment.name} 상세 정보`}
                    open={detailModalVisible}
                    onCancel={() => {
                        setDetailModalVisible(false);
                        setSelectedDepartment(null);
                    }}
                    footer={null}
                    width={900}
                >
                    <Tabs
                        defaultActiveKey="info"
                        items={[
                            {
                                key: 'info',
                                label: '기본 정보',
                                children: (
                                    <div>
                                        <Descriptions bordered column={2} size="small">
                                            <Descriptions.Item label="부서 코드" span={1}>
                                                {selectedDepartment.code}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="부서명" span={1}>
                                                {selectedDepartment.name}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="부서장" span={2}>
                                                {selectedDepartment.manager ? (
                                                    `${selectedDepartment.manager.name} (${selectedDepartment.manager.employee_id})`
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>미지정</span>
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="설명" span={2}>
                                                {selectedDepartment.description || '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="활성 상태" span={2}>
                                                {selectedDepartment.is_active ? (
                                                    <Tag color="success">활성</Tag>
                                                ) : (
                                                    <Tag color="default">비활성</Tag>
                                                )}
                                            </Descriptions.Item>
                                        </Descriptions>

                                        <Divider>예산 및 수주 목표</Divider>

                                        {selectedDepartment.budgets && selectedDepartment.budgets.length > 0 ? (
                                            <Table
                                                size="small"
                                                dataSource={selectedDepartment.budgets}
                                                rowKey="id"
                                                pagination={false}
                                                columns={[
                                                    {
                                                        title: '연도',
                                                        dataIndex: 'year',
                                                        width: 80,
                                                        align: 'center',
                                                        render: year => <strong>{year}년</strong>
                                                    },
                                                    {
                                                        title: '연간 예산',
                                                        dataIndex: 'annual_budget',
                                                        align: 'right',
                                                        render: budget => (
                                                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                                                                {parseFloat(budget || 0).toLocaleString()}원
                                                            </span>
                                                        )
                                                    },
                                                    {
                                                        title: '수주 목표',
                                                        dataIndex: 'order_target',
                                                        align: 'right',
                                                        render: target => (
                                                            <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                                                {parseFloat(target || 0).toLocaleString()}원
                                                            </span>
                                                        )
                                                    },
                                                    {
                                                        title: '목표 달성률',
                                                        width: 120,
                                                        align: 'center',
                                                        render: (_, record) => {
                                                            const budget = parseFloat(record.annual_budget || 0);
                                                            const target = parseFloat(record.order_target || 0);
                                                            const rate = budget > 0 ? (target / budget * 100).toFixed(1) : 0;
                                                            const color = rate >= 100 ? '#52c41a' : rate >= 80 ? '#faad14' : '#ff4d4f';
                                                            return (
                                                                <span style={{ color, fontWeight: 'bold' }}>
                                                                    {rate}%
                                                                </span>
                                                            );
                                                        }
                                                    }
                                                ]}
                                            />
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                                설정된 예산 정보가 없습니다.
                                                <br />
                                                예산 관리 버튼을 통해 연도별 예산과 수주 목표를 설정하세요.
                                            </div>
                                        )}

                                        <Divider>프로젝트 현황</Divider>

                                        <Descriptions bordered column={1} size="small">
                                            <Descriptions.Item label="진행 중인 프로젝트">
                                                {selectedDepartment.active_project_count}개
                                            </Descriptions.Item>
                                        </Descriptions>

                                        {selectedDepartment.active_projects && selectedDepartment.active_projects.length > 0 && (
                                            <List
                                                size="small"
                                                bordered
                                                dataSource={selectedDepartment.active_projects}
                                                renderItem={(project) => (
                                                    <List.Item>
                                                        <Space>
                                                            <Tag color="blue">{project.code}</Tag>
                                                            {project.name}
                                                            <Tag color={project.status === 'in_progress' ? 'green' : 'orange'}>
                                                                {project.status === 'in_progress' ? '진행중' : '계획중'}
                                                            </Tag>
                                                        </Space>
                                                    </List.Item>
                                                )}
                                                style={{ marginTop: 8 }}
                                            />
                                        )}
                                    </div>
                                ),
                            },
                            {
                                key: 'members',
                                label: `부서원 (${selectedDepartment.member_count}명)`,
                                children: (
                                    <div>
                                        <div style={{ marginBottom: 16 }}>
                                            <Select
                                                placeholder="부서원 추가"
                                                style={{ width: '100%' }}
                                                showSearch
                                                optionFilterProp="children"
                                                onChange={handleAddMember}
                                                value={null}
                                            >
                                                {availableUsers.map((user) => (
                                                    <Option key={user.id} value={user.id}>
                                                        {user.name} ({user.employee_id}) - {user.position || '직급 미지정'}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </div>

                                        <List
                                            bordered
                                            dataSource={selectedDepartment.employees || []}
                                            renderItem={(user) => (
                                                <List.Item
                                                    actions={[
                                                        <Button
                                                            type="link"
                                                            danger
                                                            size="small"
                                                            icon={<UserDeleteOutlined />}
                                                            onClick={() => handleRemoveMember(user)}
                                                        >
                                                            제거
                                                        </Button>,
                                                    ]}
                                                >
                                                    <List.Item.Meta
                                                        title={
                                                            <Space>
                                                                <span>{user.name}</span>
                                                                <Tag>{user.employee_id}</Tag>
                                                                {!user.is_active && <Tag color="default">비활성</Tag>}
                                                            </Space>
                                                        }
                                                        description={`${user.position || '직급 미지정'} | ${user.email}`}
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    </div>
                                ),
                            },
                        ]}
                    />
                </Modal>
            )}

            {/* 일괄 삭제 확인 모달 */}
            <Modal
                title="부서 일괄 삭제"
                open={bulkDeleteModalVisible}
                onOk={confirmBulkDelete}
                onCancel={() => setBulkDeleteModalVisible(false)}
                okText="삭제"
                okType="danger"
                cancelText="취소"
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <strong>{selectedDepartments.length}개</strong>의 부서를 삭제하시겠습니까?
                </div>
                
                <div style={{ 
                    maxHeight: 200, 
                    overflowY: 'auto', 
                    border: '1px solid #f0f0f0', 
                    borderRadius: 6,
                    padding: 12,
                    backgroundColor: '#fafafa'
                }}>
                    {selectedDepartments.map((dept) => (
                        <div key={dept.id} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            padding: '4px 0',
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <Space>
                                <strong>{dept.name}</strong>
                                <span style={{ color: '#666' }}>({dept.code})</span>
                                <Tag color="blue">{dept.member_count || 0}명</Tag>
                                {dept.budget > 0 && (
                                    <Tag color="green">{parseInt(dept.budget).toLocaleString()}원</Tag>
                                )}
                            </Space>
                        </div>
                    ))}
                </div>

                <div style={{ 
                    backgroundColor: '#fff2e8', 
                    border: '1px solid #ffb366', 
                    borderRadius: 6, 
                    padding: 12,
                    marginTop: 16
                }}>
                    <div style={{ fontWeight: 'bold', color: '#d4380d', marginBottom: 8 }}>
                        ⚠️ 주의사항
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 20, color: '#8c4a00' }}>
                        <li>부서원이 있는 부서는 삭제할 수 없습니다.</li>
                        <li>삭제된 부서는 복구할 수 없습니다.</li>
                        <li>해당 부서와 연관된 프로젝트 정보도 영향을 받을 수 있습니다.</li>
                    </ul>
                </div>
            </Modal>

            {/* 부서 예산 관리 모달 */}
            <Modal
                title={`${selectedDepartment?.name} - 예산 및 수주 목표 관리`}
                open={budgetModalVisible}
                onCancel={() => {
                    setBudgetModalVisible(false);
                    setSelectedDepartment(null);
                    budgetForm.resetFields();
                }}
                footer={null}
                width={1200}
            >
                <div>
                    {/* 기존 예산 목록 */}
                    <div style={{ marginBottom: 24 }}>
                        <h4>기존 예산 현황</h4>
                        <Table
                            loading={budgetLoading}
                            dataSource={budgets}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            columns={[
                                {
                                    title: '연도',
                                    dataIndex: 'year',
                                    width: 80,
                                    align: 'center',
                                    render: year => <strong>{year}년</strong>
                                },
                                {
                                    title: '인건비 예산',
                                    dataIndex: 'labor_budget',
                                    width: 150,
                                    align: 'right',
                                    render: (labor_budget, record) => {
                                        // labor_budget가 없으면 annual_budget 사용 (기존 데이터 호환성)
                                        const budget = parseFloat(labor_budget || record.annual_budget || 0);
                                        return (
                                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                                                {budget.toLocaleString()}원
                                            </span>
                                        );
                                    }
                                },
                                {
                                    title: '부서 인원수',
                                    dataIndex: 'employee_count',
                                    width: 100,
                                    align: 'center',
                                    render: (count, record) => {
                                        // employee_count가 없으면 현재 부서원 수 표시
                                        const actualCount = count || selectedDepartment?.member_count || 0;
                                        return `${actualCount}명`;
                                    }
                                },
                                {
                                    title: '1인당 1일 평균',
                                    key: 'daily_cost_per_person',
                                    width: 120,
                                    align: 'right',
                                    render: (_, record) => {
                                        // labor_budget가 없으면 annual_budget 사용 (기존 데이터 호환성)
                                        const laborBudget = parseFloat(record.labor_budget || record.annual_budget || 0);
                                        // employee_count가 없으면 현재 부서원 수 사용
                                        const employeeCount = parseInt(record.employee_count || selectedDepartment?.member_count || 0);
                                        
                                        if (employeeCount === 0 || laborBudget === 0) {
                                            return <span style={{ color: '#999' }}>-</span>;
                                        }
                                        
                                        const dailyCost = laborBudget / employeeCount / 365;
                                        return (
                                            <span style={{ color: '#ff7a45', fontWeight: 'bold' }}>
                                                {Math.round(dailyCost).toLocaleString()}원
                                            </span>
                                        );
                                    }
                                },
                                {
                                    title: '수주 목표',
                                    dataIndex: 'order_target',
                                    width: 150,
                                    align: 'right',
                                    render: target => (
                                        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                            {parseFloat(target || 0).toLocaleString()}원
                                        </span>
                                    )
                                },
                                {
                                    title: '비고',
                                    dataIndex: 'notes',
                                    ellipsis: true,
                                    render: notes => notes || '-'
                                },
                                {
                                    title: '수정자',
                                    dataIndex: ['updater', 'name'],
                                    width: 100,
                                    render: name => name || '-'
                                },
                                {
                                    title: '작업',
                                    width: 120,
                                    render: (_, record) => (
                                        <Space size="small">
                                            <Button
                                                type="link"
                                                size="small"
                                                onClick={() => handleBudgetEdit(record)}
                                            >
                                                수정
                                            </Button>
                                            <Button
                                                type="link"
                                                danger
                                                size="small"
                                                onClick={() => {
                                                    Modal.confirm({
                                                        title: '예산 삭제',
                                                        content: `${record.year}년 예산 정보를 삭제하시겠습니까?`,
                                                        okText: '삭제',
                                                        okType: 'danger',
                                                        onOk: () => handleBudgetDelete(record.id)
                                                    });
                                                }}
                                            >
                                                삭제
                                            </Button>
                                        </Space>
                                    )
                                }
                            ]}
                        />
                    </div>

                    <Divider />

                    {/* 예산 등록/수정 폼 */}
                    <div>
                        <h4>예산 등록/수정</h4>
                        <Form
                            form={budgetForm}
                            layout="vertical"
                            onFinish={handleBudgetSubmit}
                        >
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item
                                        name="year"
                                        label="연도"
                                        rules={[{ required: true, message: '연도를 선택하세요' }]}
                                    >
                                        <Select placeholder="연도 선택">
                                            {Array.from({ length: 10 }, (_, i) => currentYear - 2 + i).map(year => (
                                                <Option key={year} value={year}>{year}년</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="labor_budget"
                                        label="인건비 예산 (원)"
                                        rules={[{ required: true, message: '인건비 예산을 입력하세요' }]}
                                        extra={`부서원들의 연간 인건비 총액 (현재 ${selectedDepartment?.member_count || 0}명)`}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            step={1000000}
                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="order_target"
                                        label="수주 목표 (원)"
                                        rules={[{ required: true, message: '수주 목표를 입력하세요' }]}
                                        extra="부서가 달성해야 할 수주 금액"
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            step={1000000}
                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item label="&nbsp;">
                                        <Space>
                                            <Button type="primary" htmlType="submit">
                                                저장
                                            </Button>
                                            <Button onClick={() => budgetForm.resetFields()}>
                                                초기화
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                </Col>
                            </Row>
                            
                            <Form.Item
                                name="notes"
                                label="비고"
                            >
                                <TextArea rows={2} placeholder="추가 설명이나 비고 사항을 입력하세요" />
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DepartmentManagementPage;